import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy,
} from '@angular/core';
import { NestedFormGroupType } from '../../../../../../../core/models/form-group-type';
import { AttachmentFormModel } from '../../../models/attachment/attachment-form-model';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DropdownOption } from '../../../../../../../core/models/dropdown-option';
import { DropdownService } from '../../../../../../../core/services/resources/dropdown.service';
import { tap } from 'rxjs';
import { RegExPattern } from '../../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../../shared/directives/regex-input/regex-input.directive';
import { AttachmentConstants } from '../../../constants/attachment-constants';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { AttachmentFormService } from '../attachment-operation/services/attachment-form/attachment-form.service';
import { AttachmentManagementService } from '../../../services/attachment-management/attachment-management.service';

@Component({
    selector: 'app-attachment-item',
    templateUrl: './attachment-item.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
    ],
})
export class AttachmentItemComponent implements OnInit {
    @Input() applicationId!: string;
    @Input() form!: FormGroup<NestedFormGroupType<AttachmentFormModel>>;
    @Output() removeAttachment = new EventEmitter<string>();

    regExPattern = RegExPattern;

    attachmentTypeCodeOptions: DropdownOption[] = [];

    constructor(
        private readonly dropdownService: DropdownService,
        private readonly attachmentService: AttachmentService,
        private readonly attachmentFormService: AttachmentFormService,
        private readonly attachmentManagementService: AttachmentManagementService,
    ) {}

    /**
     * Determines if the attachment item is clickable based on whether it is an existing attachment.
     *
     * @return True if the attachment is existing and thus clickable; otherwise, false.
     */
    get isDownloadable(): boolean {
        return this.attachmentFormService.isExistingAttachment(this.form);
    }

    ngOnInit(): void {
        this.loadAttachmentCodeOptions();
    }

    /**
     * Returns the filename truncated to 40 characters with ellipsis if it exceeds that length.
     * @return The truncated filename or the full filename if it's 40 characters or less.
     */
    truncatedFilename(): string {
        const { filename } = this.form.value;

        return filename &&
            filename.length >
                AttachmentConstants.MAXIMUM_FILENAME_DISPLAY_LENGTH
            ? `${filename.substring(0, AttachmentConstants.MAXIMUM_FILENAME_DISPLAY_LENGTH)}...`
            : filename || '';
    }

    /**
     * Triggers the download of the attachment by calling the API and handling the Blob response.
     */
    downloadAttachment(): void {
        if (this.isDownloadable) {
            this.attachmentService
                .get(this.applicationId, this.form.controls.id.value)
                .pipe(
                    tap((blob) =>
                        this.attachmentManagementService.downloadAttachmentBlob(
                            blob,
                            this.form.controls.filename.value,
                        ),
                    ),
                )
                .subscribe();
        }
    }

    /**
     * Emits the remove attachment event with the attachment GUID.
     */
    remove(): void {
        const guid = this.form.controls.guid.value;

        if (guid) {
            this.removeAttachment.emit(guid);
        }
    }

    private loadAttachmentCodeOptions(): void {
        this.dropdownService
            .getAttachmentCodeOptions()
            .pipe(
                tap((attachmentCodeOptions: DropdownOption[]) => {
                    this.attachmentTypeCodeOptions = attachmentCodeOptions;
                }),
            )
            .subscribe();
    }
}
